import React from 'react'
import { cn } from '@/lib/utils'

interface CardProps {
  title?:       React.ReactNode
  description?: string
  actions?:     React.ReactNode
  children:     React.ReactNode
  className?:   string
  padding?:     'none' | 'sm' | 'md' | 'lg'
  noBorder?:    boolean
}

const paddingClasses = {
  none: '',
  sm:   'p-4',
  md:   'p-5',
  lg:   'p-6',
}

export const Card: React.FC<CardProps> = ({
  title,
  description,
  actions,
  children,
  className,
  padding  = 'md',
  noBorder = false,
}) => (
  <div
    className={cn(
      'bg-white dark:bg-slate-800 rounded-xl shadow-card',
      !noBorder && 'border border-slate-200 dark:border-slate-700',
      className,
    )}
  >
    {(title || actions) && (
      <div
        className={cn(
          'flex items-start justify-between gap-4',
          paddingClasses[padding],
          children && 'pb-0',
        )}
      >
        {title && (
          <div>
            {typeof title === 'string' ? (
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {title}
              </h3>
            ) : (
              title
            )}
            {description && (
              <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                {description}
              </p>
            )}
          </div>
        )}
        {actions && <div className="flex-shrink-0 flex items-center gap-2">{actions}</div>}
      </div>
    )}

    <div className={cn(paddingClasses[padding])}>{children}</div>
  </div>
)

// ─── CardHeader / CardBody / CardFooter sub-components ───────────────────────
export const CardHeader: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'px-5 py-4 border-b border-slate-100 dark:border-slate-700',
      className,
    )}
  >
    {children}
  </div>
)

export const CardBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cn('p-5', className)}>{children}</div>

export const CardFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'px-5 py-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 rounded-b-xl',
      className,
    )}
  >
    {children}
  </div>
)
